import { Controller } from '@hotwired/stimulus'

/**
 * Reveal Controller
 * Fades a section in as it enters the viewport.
 *
 * Degrades safely: content is visible by default in CSS (.c-lp-reveal has
 * no hiding rule of its own). The hiding rule is scoped under a
 * .c-lp-reveal-ready class on <html> that ONLY this controller ever sets,
 * and only on the path where it is actually about to animate. So: no JS,
 * JS blocked, JS erroring before Stimulus connects, IntersectionObserver
 * missing, or reduced motion — nothing is ever hidden. A visitor only ever
 * sees the fade if a live controller is running it.
 *
 *   <section data-controller="public--reveal" class="c-lp-reveal">
 */
export default class extends Controller {
  connect() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduced || typeof IntersectionObserver === 'undefined') {
      this.element.classList.add('is-visible')
      return
    }

    // Only opt into the CSS hiding rule once we are actually going to
    // animate — never on the early-return path above.
    document.documentElement.classList.add('c-lp-reveal-ready')

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        this.observer.unobserve(entry.target)
      })
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }
}
